import { z } from "zod";

export const dataKeySchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]*$/)
  .refine(
    (value) => !["constructor", "prototype", "__proto__"].includes(value),
  );
export const integrationRecordIdSchema = z
  .string()
  .min(1)
  .max(200)
  .refine(
    (value) =>
      value.trim() === value &&
      !/[\x00-\x1f\x7f]/.test(value) &&
      !["constructor", "prototype", "__proto__"].includes(value),
  );
export const integrationDataPointerSchema = z
  .string()
  .max(500)
  .refine(
    (value) =>
      value === "" ||
      (value.startsWith("/") &&
        !value
          .split("/")
          .some(
            (part) =>
              /~(?![01])/.test(part) ||
              ["__proto__", "constructor", "prototype"].includes(
                part.replace(/~1/g, "/").replace(/~0/g, "~"),
              ),
          )),
  );
export const integrationDataFieldSchema = z
  .object({
    key: dataKeySchema,
    label: z.string().trim().min(1).max(100),
    mode: z.enum(["value", "choices"]).default("value"),
    path: integrationDataPointerSchema.default(""),
    valuePath: integrationDataPointerSchema.default("/id"),
    labelPath: integrationDataPointerSchema.default("/name"),
    selectedPath: integrationDataPointerSchema.optional(),
  })
  .strict();
export const integrationDataSourceSchema = z
  .object({
    id: dataKeySchema,
    name: z.string().trim().min(1).max(100),
    mode: z.enum(["host", "http"]),
    endpoint: z.string().max(2048).optional(),
    httpMethod: z.enum(["GET", "POST"]).optional(),
    requestStyle: z.enum(["envelope", "parameters"]).optional(),
    parameterDefaults: z
      .record(
        dataKeySchema,
        z.union([
          z.string().max(1000),
          z.number().finite(),
          z.boolean(),
          z.null(),
        ]),
      )
      .refine((value) => Object.keys(value).length <= 30)
      .optional(),
    authHeader: z
      .string()
      .regex(/^[A-Za-z][A-Za-z0-9-]{0,63}$/)
      .default("Authorization")
      .refine(
        (value) =>
          ![
            "host",
            "cookie",
            "content-length",
            "content-type",
            "connection",
            "transfer-encoding",
            "proxy-authorization",
            "accept-encoding",
            "content-encoding",
            "upgrade",
            "te",
            "trailer",
            "proxy-connection",
            "keep-alive",
          ].includes(value.toLowerCase()),
      ),
    recordIds: z
      .array(integrationRecordIdSchema)
      .min(1)
      .max(100)
      .nullable()
      .default(null),
    parameterKeys: z.array(dataKeySchema).max(30).default([]),
    fields: z
      .array(integrationDataFieldSchema)
      .min(1)
      .max(100)
      .refine(
        (fields) =>
          new Set(fields.map((field) => field.key)).size === fields.length,
      ),
  })
  .strict()
  .superRefine((source, ctx) => {
    if (
      source.parameterDefaults &&
      Object.keys(source.parameterDefaults).some(
        (key) => !source.parameterKeys.includes(key),
      )
    )
      ctx.addIssue({
        code: "custom",
        message: "Default parameters must be permitted parameter keys",
        path: ["parameterDefaults"],
      });
    if (source.httpMethod === "GET" && source.requestStyle !== "parameters")
      ctx.addIssue({
        code: "custom",
        message: "GET queries require parameter delivery",
        path: ["requestStyle"],
      });
    if (source.mode === "http") {
      try {
        const url = new URL(source.endpoint || "");
        if (
          url.protocol !== "https:" ||
          url.username ||
          url.password ||
          url.hash ||
          url.search ||
          (url.port && url.port !== "443")
        )
          throw Error();
      } catch {
        ctx.addIssue({
          code: "custom",
          message:
            "Use a fixed HTTPS endpoint without credentials, query, fragment, or custom port",
          path: ["endpoint"],
        });
      }
    } else if (source.endpoint !== undefined)
      ctx.addIssue({
        code: "custom",
        message: "Host sources do not have an endpoint",
        path: ["endpoint"],
      });
  });
export const integrationDataSourcesSchema = z
  .array(integrationDataSourceSchema)
  .max(20)
  .refine(
    (sources) =>
      new Set(sources.map((source) => source.id)).size === sources.length,
  );
export const integrationDataGrantSchema = z
  .object({
    sourceId: dataKeySchema,
    recordIds: z.array(integrationRecordIdSchema).min(1).max(100),
    fields: z.array(dataKeySchema).min(1).max(100),
  })
  .strict();
export const integrationDataGrantsSchema = z
  .array(integrationDataGrantSchema)
  .max(100)
  .refine((grants) => {
    const sources = new Set(grants.map((grant) => grant.sourceId));
    return (
      sources.size <= 20 &&
      [...sources].every(
        (source) =>
          new Set(
            grants
              .filter((grant) => grant.sourceId === source)
              .flatMap((grant) => grant.recordIds),
          ).size <= 100,
      )
    );
  });

export type IntegrationDataSource = z.output<
  typeof integrationDataSourceSchema
>;
